import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errorText } from "@/lib/api";

// Suggest the stable part of a bank description: drop card numbers, dates and references.
export const suggestMatch = (text) =>
  text.toUpperCase().replace(/VALUE DATE:?\s*\S+/g, " ").replace(/CARD\s+XX\w+/g, " ")
    .split(/\s+/).filter((w) => w && !/\d/.test(w)).slice(0, 4).join(" ");

export default function SaveRuleDialog({ row, onClose, onSaved }) {
  const api = useApi();
  const [match, setMatch] = useState(() => suggestMatch(row.original || row.description));
  const [rename, setRename] = useState(row.description);
  const [busy, setBusy] = useState(false);
  const text = (row.original || "").toUpperCase().split(/\s+/).join(" ");
  const fits = match.trim() && text.includes(match.trim().toUpperCase().split(/\s+/).join(" "));

  const save = async () => {
    setBusy(true);
    try {
      await api("post", "/payee_rules", { name: match.trim(), rename: rename.trim(), active: true });
      toast.success(`Rule saved: "${match.trim()}" → ${rename.trim()}`);
      onSaved({ match: match.trim(), rename: rename.trim() });
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="save-rule-dialog">
        <DialogHeader>
          <DialogTitle>Save payee rule</DialogTitle>
          <DialogDescription>Every future bank line containing this text gets the new description.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-muted-foreground font-mono break-all">Bank: {row.original}</div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-match">When the bank text contains</Label>
            <Input id="rule-match" value={match} onChange={(e) => setMatch(e.target.value)} data-testid="save-rule-match-input" />
            {!fits && <p className="text-xs text-amber-500">This line's bank text doesn't contain that, so the rule won't catch it.</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-rename">Change the description to</Label>
            <Input id="rule-rename" value={rename} onChange={(e) => setRename(e.target.value)} data-testid="save-rule-rename-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="save-rule-cancel">Cancel</Button>
          <Button disabled={busy || match.trim().length < 2 || !rename.trim()} onClick={save} data-testid="save-rule-submit">
            {busy ? "Saving…" : "Save rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
